import React, { useState, useEffect } from 'react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { Box, IconButton, useToast, Image } from "@chakra-ui/react";
import './customFileUploader.css';

interface CustomFileUploaderProps {
    loading?: boolean;
    deleting?: boolean;
    values?: any[];
    onChange?: Function;
    className?: string;
    placeholderIcon?: React.ReactNode | string;
    disabled?: boolean;
    showDefaultContent?: boolean;
    loadingStates?: boolean;
    enumeration?: boolean;
    draggable?: boolean;
    scroll?: any;
    read?: boolean;
    showInvalids?: boolean;
    bigPictures?: boolean;
    selectedPicture?: any;
    setSelectedPicture?: any;
    onDeleteItem?: Function;
    onAddItem?: Function;
    onMoveItem?: Function;
    reloadedName?: string;
    onPressReload?: Function;
    multiple?: boolean;
    hideDeleteButton?: boolean;
}

const CustomFileUploader = ({
   values,
   onChange = () => {},
   className,
   placeholderIcon,
   disabled,
   loadingStates = true,
   enumeration = true,
   draggable = true,
   scroll = null,
   read = false,
   showInvalids = false,
   bigPictures = false,
   selectedPicture,
   setSelectedPicture,
   onDeleteItem,
   onAddItem,
   onMoveItem,
   reloadedName,
   onPressReload,
   showDefaultContent = false,
   multiple = false,
   loading = false,
   deleting = false,
   hideDeleteButton = false,
}: CustomFileUploaderProps) => {
   const [localImage, setLocalImage] = useState<string | null>(null);
   const toast = useToast();

   useEffect(() => {
       if (!values || values.length === 0) {
           setLocalImage(null);
           return;
       }
       
       const imageUrl = values[0]?.previewURL || values[0]?.url;
       if (imageUrl) {
           setLocalImage(imageUrl);
       } else {
           setLocalImage(null);
       }
   }, [values]);

   const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
       if (disabled) {
           toast({
               title: "Has alcanzado el límite máximo de imágenes permitidas",
               status: "error",
               duration: 5000,
               isClosable: true,
               position: "top-right",
           });
           return;
       }

       const files = event.target.files;
       if (!files || files.length === 0) return;

       const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
       const newImages: any[] = [];

       Array.from(files).forEach((file, index) => {
           if (!allowedTypes.includes(file.type)) {
               toast({
                   title: "Formato de archivo no válido. Solo se permiten imágenes JPEG, PNG o WEBP.",
                   status: "error",
                   duration: 5000,
                   isClosable: true,
                   position: "top-right",
               });
               return;
           }

           if (file.size === 0) {
               toast({
                   title: "Archivo vacío no permitido.",
                   status: "error",
                   duration: 5000,
                   isClosable: true,
                   position: "top-right",
               });
               return;
           }

           const previewURL = URL.createObjectURL(file);
           newImages.push({
               file: file,
               id: Math.floor((index + Math.random()) * 0x10000).toString(16),
               previewURL,
               name: `${Math.floor((index + Math.random()) * 0x10000).toString(16)}.jpg`,
               upload: loadingStates ? "loading" : "success",
               toDelete: false
           });
       });

       if (newImages.length > 0) {
           const updatedValues = multiple
               ? [...(values || []), ...newImages]
               : [newImages[0]];

           setLocalImage(newImages[0]?.previewURL);
           onChange(updatedValues);
       }

       event.target.value = "";
   };

   const handleRemoveImage = (e: React.MouseEvent) => {
       e.stopPropagation();
       e.preventDefault();

       setLocalImage(null);

       if (onDeleteItem) {
           const currentValue = values && values.length > 0 ? values[0] : null;
           if (currentValue) {
               onDeleteItem(currentValue);
           }
       } else {
           onChange([]);
       }
   };

   const displayImage = localImage;

   return (
       <div className={`customFileUploader ${className || ''}`}>
           <div className="uploader">
               {!displayImage && (
                   <input
                       type="file"
                       onChange={handleFileChange}
                       accept="image/*"
                       disabled={disabled}
                       className="input"
                       multiple={multiple}
                   />
               )}

               {!displayImage ? (
                   <div className="placeholder">
                       {placeholderIcon ? (
                           <Box display="flex" flexDirection="column" alignItems="center">
                               {typeof placeholderIcon === 'string' ? (
                                   <Image src={placeholderIcon} alt="Placeholder" boxSize="48px" />
                               ) : (
                                   placeholderIcon
                               )}
                               <p>Selecciona para cargar</p>
                           </Box>
                       ) : (
                           <AddIcon boxSize="32px" />
                       )}
                   </div>
               ) : (
                   <div className={`preview ${loading ? 'loading' : ''}`}>
                       <img src={displayImage} alt="Vista previa" />

                       {loading ? (
                           <div className="loaderOverlay">
                               <span className="loader"></span>
                           </div>
                       ) : (
                           !hideDeleteButton && (
                               <IconButton
                                   aria-label="Delete"
                                   className="delete"
                                   onClick={handleRemoveImage}
                                   isDisabled={deleting}
                                   size="sm"
                                   variant="ghost"
                               >
                                   <DeleteIcon boxSize="18px" className="icon" />
                               </IconButton>
                           )
                       )}
                   </div>
               )}
           </div>
       </div>
   );
};

export default CustomFileUploader;
